'use client';

import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface IntegrationItem {
  id: string;
  name: string;
  icon_url?: string;
  category: string;
}

export default function IntegracjeSection() {
  const [content, setContent] = useState({
    title: 'Łączymy wszystko, czego używasz.',
    subtitle: 'Konfigurujemy gotowe integracje BaseLinker i Apilo oraz budujemy niestandardowe połączenia przez API.',
  });
  const [integrations, setIntegrations] = useState<IntegrationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch content
        const { data: settingsData, error: settingsError } = await supabase
          .from('settings')
          .select('key, value')
          .in('key', ['integracje_title', 'integracje_subtitle']);

        if (settingsError) {
          console.error('Error fetching integracje content:', settingsError.message);
        } else {
          const settingsMap: Record<string, string> = {};
          settingsData?.forEach((setting) => {
            settingsMap[setting.key] = setting.value;
          });

          setContent({
            title: settingsMap['integracje_title'] || content.title,
            subtitle: settingsMap['integracje_subtitle'] || content.subtitle,
          });
        }

        // Fetch integrations
        const { data: integrationsData, error: integrationsError } = await supabase
          .from('integration_icons')
          .select('*')
          .eq('is_active', true)
          .order('order_position', { ascending: true });

        if (integrationsError) {
          console.error('Error fetching integrations:', integrationsError.message);
        } else {
          setIntegrations(integrationsData || []);
        }
      } catch (err) {
        console.error('Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [supabase]);

  return (
    <section id="integracje" className="py-14 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="mb-14 grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-6 space-y-3">
            <p className="text-xs font-700 uppercase tracking-wide text-blue-DEFAULT" style={{ fontFamily: 'Inter, sans-serif' }}>Integracje</p>
            <h2 className="text-4xl md:text-5xl font-700 tracking-tight text-navy leading-[1.0]" style={{ fontFamily: 'Inter, sans-serif' }}>
              {content.title}
            </h2>
          </div>
          <div className="lg:col-span-6">
            <p className="text-base text-muted font-400 leading-relaxed max-w-md" style={{ fontFamily: 'Inter, sans-serif' }}>
              {content.subtitle}
            </p>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
          {integrations?.map((integration) => (
            <div
              key={integration.id}
              className="group bg-white rounded-2xl p-6 border border-gray-100 hover:border-blue-DEFAULT hover:shadow-lg transition-all duration-300"
            >
              <div className="flex flex-col items-center text-center gap-4">
                <div className="w-16 h-16 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-DEFAULT transition-colors">
                  <Icon
                    name="CubeIcon"
                    size={32}
                    className="text-blue-DEFAULT group-hover:text-white transition-colors"
                    variant="outline"
                  />
                </div>
                <div>
                  <h3 className="text-base font-700 text-navy">{integration.name}</h3>
                  <p className="text-xs text-muted font-500 mt-1">{integration.category}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Note */}
        <p className="mt-8 text-center text-sm text-muted font-400" style={{ fontFamily: 'Inter, sans-serif' }}>
          i wiele więcej — lista integracji BaseLinker przekracza{' '}
          <strong className="text-navy font-500">1000 połączeń</strong>.
        </p>
      </div>
    </section>
  );
}